'use client';

import { useEffect, useState, useCallback, useMemo, startTransition } from 'react';
import { motion } from 'framer-motion';
import { ThemedImage } from '@/components/ui/themed-image';
import { 
  Plus, 
  Minus, 
  Check,
  ShoppingBag,
  Spinner,
  X,
} from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { useCheckoutStore, selectExtras, type ExtraItem } from '@/stores/checkout-store';
import { useCartStore } from '@/stores/cart-store';
import { apiClient } from '@/lib/api';

// ========== CONSTANTS ==========

// Список допов ОДИН и тот же, что в приложении (azia_app/src/components/cart/ExtrasStep.tsx):
// 1-7 — бесплатные (соевый соус, васаби, имбирь, палочки…), 103-107 — платные соусы.
// Раньше на сайте было [1, 2, 3, 4, 7] — платные соусы не предлагались нигде,
// то есть сайт был лишён допродажи, которая в приложении работает.
// Недоступные/скрытые позиции отсеиваются ниже по is_visible и status,
// поэтому лишний id в списке безопасен: он просто не попадёт в выдачу.
const EXTRAS_PRODUCT_IDS = [1, 2, 3, 4, 5, 6, 7, 103, 104, 105, 106, 107];

// ========== TYPES ==========

interface ExtrasStepProps {
  onNext?: () => void;
  onBack?: () => void;
}

interface ApiProduct {
  id: number;
  name: string;
  price: number;
  image_url: string;
  sku?: string;
  is_visible?: boolean | number | string;
  status?: string;
}

// ========== HELPER FUNCTIONS ==========

const formatPrice = (price: number): string => {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
};

// ========== EXTRA PRODUCT CARD ==========

function ExtraProductCard({
  product,
  quantity,
  onAdd,
  onRemove,
  onUpdateQuantity,
  index,
  isDark,
}: {
  product: ExtraItem;
  quantity: number;
  onAdd: () => void;
  onRemove: () => void;
  onUpdateQuantity: (qty: number) => void;
  index: number;
  isDark: boolean;
}) {
  const isInCart = quantity > 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="w-[calc(50%-6px)]"
    >
      <div
        className={cn(
          'relative rounded-card overflow-hidden border transition-all',
          isDark 
            ? 'bg-[var(--surface-secondary)] border-[var(--border-default)]' 
            : 'bg-[var(--surface-primary)] border-[var(--border-default)]',
          isInCart && 'border-[var(--primary)]'
        )}
      >
        {/* Quantity Badge */}
        {isInCart && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-2 left-2 z-10 w-6 h-6 rounded-pill bg-[var(--primary)] text-[var(--text-inverse)] text-xs font-bold flex items-center justify-center"
          >
            {quantity}
          </motion.div>
        )}
        
        {/* Check Badge */}
        {isInCart && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-2 right-2 z-10 w-6 h-6 rounded-pill bg-[var(--pink-500)] text-[var(--text-inverse)] flex items-center justify-center"
          >
            <Check weight="bold" className="w-3.5 h-3.5" />
          </motion.div>
        )}
        
        {/* Image */}
        <div className={cn(
          'relative h-28 w-full',
          isDark ? 'bg-[var(--surface-tertiary)]' : 'bg-[var(--surface-secondary)]'
        )}>
          <ThemedImage
            src={product.image_url}
            alt={product.name}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 50vw, 200px"
          />
        </div>
        
        {/* Info */}
        <div className="p-3">
          <h4 className={cn(
            'text-xs font-medium mb-2 line-clamp-2 h-8',
            isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-primary)]'
          )}>
            {product.name}
          </h4>
          
          <div className="flex items-center justify-between">
            <span className={cn(
              'text-sm font-bold font-mono',
              isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-primary)]'
            )}>
              {formatPrice(product.price)}
            </span>
            
            {/* Controls */}
            {quantity === 0 ? (
              <motion.button
                onClick={onAdd}
                className="w-7 h-7 rounded-pill bg-[var(--primary)] text-[var(--text-inverse)] flex items-center justify-center"
                whileTap={{ scale: 0.9 }}
              >
                <Plus weight="bold" className="w-4 h-4" />
              </motion.button>
            ) : (
              <div className="flex items-center gap-1">
                <motion.button
                  onClick={() => quantity === 1 ? onRemove() : onUpdateQuantity(quantity - 1)}
                  className={cn(
                    'w-6 h-6 rounded-pill flex items-center justify-center',
                    isDark ? 'bg-[var(--primary-subtle)] text-[var(--primary)]' : 'bg-[var(--violet-50)] text-[var(--primary)]'
                  )}
                  whileTap={{ scale: 0.9 }}
                >
                  <Minus weight="bold" className="w-3 h-3" />
                </motion.button>
                
                <span className={cn(
                  'w-5 text-center text-xs font-semibold',
                  isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-primary)]'
                )}>
                  {quantity}
                </span>
                
                <motion.button
                  onClick={() => onUpdateQuantity(quantity + 1)}
                  className={cn(
                    'w-6 h-6 rounded-pill flex items-center justify-center',
                    isDark ? 'bg-[var(--primary-subtle)] text-[var(--primary)]' : 'bg-[var(--violet-50)] text-[var(--primary)]'
                  )}
                  whileTap={{ scale: 0.9 }}
                >
                  <Plus weight="bold" className="w-3 h-3" />
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ========== MAIN COMPONENT ==========

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function ExtrasStep(_props: ExtrasStepProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableExtras, setAvailableExtras] = useState<ExtraItem[]>([]);
  
  // Checkout Store
  const extras = useCheckoutStore(selectExtras);
  const setExtras = useCheckoutStore((state) => state.setExtras);
  const updateExtraQuantity = useCheckoutStore((state) => state.updateExtraQuantity);
  
  // Cart Store (for syncing)
  const addItem = useCartStore((state) => state.addItem);
  const removeItem = useCartStore((state) => state.removeItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  
  // Load extras
  useEffect(() => {
    const loadExtras = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const loadPromises = EXTRAS_PRODUCT_IDS.map(async (id) => {
          try {
            const product = await apiClient.get<ApiProduct>(`/products/${id}`);
            return product;
          } catch {
            return null;
          }
        });
        
        const results = await Promise.all(loadPromises);
        const validProducts: ExtraItem[] = results
          .filter((p): p is ApiProduct => {
            if (!p) return false;
            // Check visibility
            const isVisible = p.is_visible === 1 || p.is_visible === true || p.is_visible === '1';
            const isAvailable = !p.status || p.status === 'available';
            return isVisible && isAvailable;
          })
          .map((p) => ({
            id: p.id,
            name: p.name,
            price: p.price,
            quantity: extras.find(e => e.id === p.id)?.quantity || 0,
            image_url: p.image_url,
            sku: p.sku,
          }));
        
        setAvailableExtras(validProducts);

        // Список допов в сторе ПЕРЕСОБИРАЕМ всегда, а не только когда он пуст.
        // Стор переживает перезагрузку (persist в localStorage), и у человека,
        // заходившего раньше, там лежит прежний набор из пяти позиций. Позиции,
        // которых в нём нет, updateExtraQuantity молча игнорирует — счётчик
        // остаётся нулём, карточка так и предлагает «добавить», а в корзину
        // при каждом нажатии падает ещё одна штука. Количества при пересборке
        // не теряются: validProducts берёт их из текущего стора.
        setExtras(validProducts);
      } catch {
        setError('Не удалось загрузить дополнительные товары');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadExtras();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Get quantity for product
  const getQuantity = useCallback((productId: number) => {
    return extras.find(e => e.id === productId)?.quantity || 0;
  }, [extras]);
  
  // Handle add с startTransition
  const handleAdd = useCallback((product: ExtraItem) => {
    startTransition(() => {
      updateExtraQuantity(product.id, 1);
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
        is_available: true,
        status: 'available',
        spiciness: 'none',
        sku: product.sku || '',
        description: '',
        categories: [],
      }, 1);
    });
  }, [updateExtraQuantity, addItem]);
  
  // Handle remove с startTransition
  const handleRemove = useCallback((productId: number) => {
    startTransition(() => {
      updateExtraQuantity(productId, 0);
      removeItem(productId);
    });
  }, [updateExtraQuantity, removeItem]);
  
  // Handle quantity change с startTransition
  const handleQuantityChange = useCallback((productId: number, quantity: number) => {
    startTransition(() => {
      updateExtraQuantity(productId, quantity);
      updateQuantity(productId, quantity);
    });
  }, [updateExtraQuantity, updateQuantity]);
  
  // Мемоизированные вычисления totals
  const totalExtrasCount = useMemo(() => extras.reduce((sum, e) => sum + e.quantity, 0), [extras]);
  const totalExtrasPrice = useMemo(() => extras.reduce((sum, e) => sum + e.price * e.quantity, 0), [extras]);
  const formattedTotalExtrasPrice = useMemo(() => formatPrice(totalExtrasPrice), [totalExtrasPrice]);
  
  return (
    <div className="p-4">
      {/* Header Card */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          'p-3 rounded-card mb-4 border',
          isDark 
            ? 'bg-[var(--pink-500)]/10 border-[var(--pink-500)]/20' 
            : 'bg-gradient-to-r from-[var(--pink-500)]/10 to-[var(--rose-500)]/10 border-[var(--pink-500)]/20'
        )}
      >
        <h3 className={cn(
          'font-semibold text-sm mb-1',
          isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-primary)]'
        )}>
          Добавить к заказу
        </h3>
        <p className={cn(
          'text-sm',
          isDark ? 'text-[var(--text-secondary)]' : 'text-[var(--text-secondary)]'
        )}>
          Дополните свой заказ вкусными добавками
        </p>
        
        {totalExtrasCount > 0 && (
          <div className={cn(
            'mt-3 pt-3 border-t flex items-center justify-between',
            isDark ? 'border-[var(--pink-500)]/20' : 'border-[var(--pink-500)]/20'
          )}>
            <span className="text-xs text-[var(--pink-500)] font-medium">
              Выбрано: {totalExtrasCount} шт
            </span>
            <span className="text-sm font-bold text-[var(--pink-500)]">
              {formattedTotalExtrasPrice}
            </span>
          </div>
        )}
      </motion.div>
      
      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Spinner weight="bold" className="w-8 h-8 text-[var(--primary)] animate-spin mb-3" />
          <p className={cn(
            'text-sm',
            isDark ? 'text-[var(--text-secondary)]' : 'text-[var(--text-secondary)]'
          )}>
            Загрузка дополнительных товаров...
          </p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-8">
          <div className={cn(
            'w-16 h-16 rounded-pill flex items-center justify-center mb-3',
            isDark ? 'bg-[var(--error)]/10' : 'bg-[var(--error-bg)]'
          )}>
            <X weight="bold" className="w-8 h-8 text-[var(--error)]" />
          </div>
          <p className={cn(
            'font-medium mb-2',
            'text-[var(--text-primary)]'
          )}>
            Ошибка загрузки
          </p>
          <p className={cn(
            'text-sm text-center mb-4',
            isDark ? 'text-[var(--text-secondary)]' : 'text-[var(--text-secondary)]'
          )}>
            {error}
          </p>
          <motion.button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[var(--primary)] text-[var(--text-inverse)] text-sm font-medium rounded-card"
            whileTap={{ scale: 0.95 }}
          >
            Повторить
          </motion.button>
        </div>
      ) : availableExtras.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <div className={cn(
            'w-16 h-16 rounded-pill flex items-center justify-center mb-3',
            isDark ? 'bg-[var(--surface-secondary)]' : 'bg-[var(--surface-secondary)]'
          )}>
            <ShoppingBag weight="fill" className={cn(
              'w-8 h-8',
              isDark ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-tertiary)]'
            )} />
          </div>
          <p className={cn(
            'font-medium mb-1',
            'text-[var(--text-primary)]'
          )}>
            Нет доступных товаров
          </p>
          <p className={cn(
            'text-sm',
            isDark ? 'text-[var(--text-secondary)]' : 'text-[var(--text-secondary)]'
          )}>
            Дополнительные товары сейчас недоступны
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {availableExtras.map((product, index) => (
            <ExtraProductCard
              key={product.id}
              product={product}
              quantity={getQuantity(product.id)}
              onAdd={() => handleAdd(product)}
              onRemove={() => handleRemove(product.id)}
              onUpdateQuantity={(qty) => handleQuantityChange(product.id, qty)}
              index={index}
              isDark={isDark}
            />
          ))}
        </div>
      )}
    </div>
  );
}

